import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Snackbar, IconButton, Box, useMediaQuery, useTheme, Grow } from '@mui/material';
import CloseIcon from '../../assets/icons/close-small.svg';
import useStyles from './ToastStyles';

function PopTransition(props) {
  return (
    <Grow
      {...props}
      style={{ transformOrigin: 'center bottom' }}
      timeout={{ enter: 220, exit: 160 }}
    />
  );
}

const Toast = ({ open, onClose, message, maxWidth, width, height, autoHideDuration }) => {
  const classes = useStyles();
  const theme = useTheme();
  const breakpointMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const [displayMessage, setDisplayMessage] = useState('');

  useEffect(() => {
    if (open && message) {
      setDisplayMessage(message);
    }
  }, [open, message]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    onClose();
  };

  const handleExited = () => {
    setDisplayMessage('');
  };

  // On desktop without explicit width: fit-content so toast grows with text, otherwise wraps a little weird and padding sucks
  const toastWidth = width || (breakpointMdDown ? '100%' : 'fit-content');
  const toastMaxWidth = maxWidth || (breakpointMdDown ? '100%' : width || 'min(720px, 90vw)');
  const toastHeight = height || 'auto';
  const toastDuration = autoHideDuration || 5000;

  return (
    <Snackbar
      open={open}
      autoHideDuration={toastDuration}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionComponent={PopTransition}
      TransitionProps={{ onExited: handleExited }}
      sx={{
        '& .MuiSnackbarContent-root': {
          minWidth: 'auto',
          padding: 0,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          width: toastWidth,
          maxWidth: toastMaxWidth,
        },
      }}
    >
      <Box className={classes.toastContainer} sx={{ height: toastHeight }}>
        <Box className={classes.toastMessage}>{displayMessage}</Box>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          size='small'
          aria-label='close'
        >
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </Box>
    </Snackbar>
  );
};

Toast.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  message: PropTypes.string.isRequired,
  maxWidth: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
  autoHideDuration: PropTypes.number,
};

export default Toast;
